import { Check, LoaderCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { authClient } from "./auth-client";
import { getAuthErrorMessage } from "./auth-errors";
import { useAuth } from "./AuthProvider";
import { AuthShell } from "./AuthShell";

type VerificationState =
  | { status: "working" }
  | { status: "complete" }
  | { status: "error"; message: string };

export const VerifyEmailPage = () => {
  const location = useLocation();
  const { refresh } = useAuth();
  const started = useRef(false);
  const token = new URLSearchParams(location.search).get("token") ?? "";
  const [state, setState] = useState<VerificationState>({
    status: "working",
  });

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!token) {
      setState({
        status: "error",
        message: "This verification link is incomplete.",
      });
      return;
    }

    void authClient
      .verifyEmail({
        query: { token },
      })
      .then(async (result) => {
        if (result.error) {
          setState({
            status: "error",
            message: getAuthErrorMessage(
              result.error,
              "We could not verify this email.",
            ),
          });
          return;
        }

        await refresh();
        setState({ status: "complete" });
      })
      .catch(() => {
        setState({
          status: "error",
          message: "We could not verify this email.",
        });
      });
  }, [refresh, token]);

  return (
    <AuthShell
      title={
        state.status === "working"
          ? "Verifying your email"
          : state.status === "complete"
            ? "Email verified"
            : "Verification failed"
      }
    >
      <section className="auth-success" aria-live="polite">
        <span className="auth-success__icon" aria-hidden="true">
          {state.status === "working" ? (
            <LoaderCircle className="auth-spinner" size={21} />
          ) : (
            <Check size={21} />
          )}
        </span>
        {state.status === "working" ? (
          <p>Finishing your Reelio account…</p>
        ) : state.status === "complete" ? (
          <>
            <h2>Your workspace is ready.</h2>
            <p>You are signed in and can continue to Reelio.</p>
            <Link className="auth-submit" to="/app">
              Open workspace
            </Link>
          </>
        ) : (
          <>
            <h2>The link could not be used.</h2>
            <p>{state.message}</p>
            <Link className="auth-secondary" to="/auth/login">
              Return to login
            </Link>
          </>
        )}
      </section>
    </AuthShell>
  );
};
